import { BaseCommand } from './BaseCommand';
import type { PromptCommandCategory } from './PromptCommandCategory';
import { ViewCommand } from './ViewCommand';
import { PromptEdit } from '~/components/chat/commands/views/PromptEdit';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import type { CommandShortcut } from '~/lib/stores/command/types';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

export class UserPromptCommand extends BaseCommand {
  prompt: string;
  category?: PromptCommandCategory;

  constructor(details: { id: string; name: string; icon?: string; prompt: string; category?: PromptCommandCategory }) {
    super({ id: details.id, name: details.name, icon: details.icon });
    this.prompt = details.prompt;
    this.category = details.category;
  }

  action({ setInput }: { setInput: (input: string) => void }) {
    setInput(this.prompt);

    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.PromptLibraryPromptUsed,
        properties: {
          promptId: this.id,
          promptTitle: this.name,
          categoryId: this.category?.id,
          categoryName: this.category?.name,
        },
      },
    });
  }

  shortcuts: CommandShortcut[] = [
    {
      label: 'Use Prompt',
      keys: ['Enter'],
      action: (context) => this.action(context),
    } satisfies CommandShortcut,
    {
      label: 'Edit',
      keys: ['Meta', 'E'],
      action: ({ commandStore, focusPromptInput }) => {
        const editCommand = new ViewCommand({
          id: 'edit_prompt',
          name: 'Edit Prompt',
          icon: 'i-lucide:message-square',
          view: () => (
            <PromptEdit
              command={this}
              categories={commandStore.$promptCategories.get()}
              onCancel={() => {
                commandStore.router.pop();
                focusPromptInput();
              }}
              onSave={(values) => {
                const category = commandStore.findPromptCategoryById(values.category);
                commandStore.updateCustomPrompt(this.id, {
                  ...values,
                  icon: values.icon || 'i-lucide:message-square',
                  category: category?.id,
                });

                sendAnalyticsEvent({
                  action: AnalyticsAction.Track,
                  payload: {
                    event: AnalyticsTrackEvent.PromptLibraryPromptUpdated,
                    properties: {
                      promptId: this.id,
                      promptTitle: this.name,
                      categoryId: this.category?.id,
                      categoryName: this.category?.name,
                    },
                  },
                });

                commandStore.router.pop();
                focusPromptInput();
              }}
            />
          ),
        });

        commandStore.router.push(editCommand);
      },
    } satisfies CommandShortcut,
    {
      label: 'Delete',
      keys: ['Meta', 'Del'],
      action: ({ commandStore }) => {
        commandStore.removeCustomPrompt(this.id);
        commandStore.setActiveIndex((index) => Math.max(0, index - 1));
      },
    } satisfies CommandShortcut,
    {
      id: 'favorite',
      label: 'Toggle Favorite',
      keys: ['Meta', 'Shift', 'L'],
      action: ({ commandStore }) => {
        commandStore.toggleFavorite(this.id);

        sendAnalyticsEvent({
          action: AnalyticsAction.Track,
          payload: {
            event: AnalyticsTrackEvent.PromptLibraryPromptStarred,
            properties: {
              promptId: this.id,
              promptTitle: this.name,
              categoryId: this.category?.id,
              categoryName: this.category?.name,
            },
          },
        });
      },
    } satisfies CommandShortcut,
  ];
}
